import {
  Switch as AriaSwitch,
  type SwitchProps as AriaSwitchProps,
} from 'react-aria-components'
import { cn } from '@/lib/utils'

export interface SwitchProps extends AriaSwitchProps {
  label?: string
  description?: string
  size?: 'sm' | 'md'
}

const switchSizes = {
  sm: {
    track: 'h-5 w-9',
    thumb: 'h-4 w-4 selected:translate-x-4',
  },
  md: {
    track: 'h-6 w-11',
    thumb: 'h-5 w-5 selected:translate-x-5',
  },
}

export function Switch({
  className,
  label,
  description,
  size = 'md',
  children,
  ...props
}: SwitchProps) {
  const sizeClasses = switchSizes[size]

  return (
    <AriaSwitch
      className={cn(
        'group flex items-center gap-3 cursor-pointer',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    >
      {({ isSelected }) => (
        <>
          <div
            className={cn(
              'relative inline-flex shrink-0 rounded-full transition-colors',
              sizeClasses.track,
              isSelected
                ? 'bg-brand-500 dark:bg-brand-600'
                : 'bg-gray-200 dark:bg-gray-700',
              'group-focus-visible:ring-2 group-focus-visible:ring-brand-500 group-focus-visible:ring-offset-2'
            )}
          >
            <span
              className={cn(
                'absolute left-0.5 top-0.5 rounded-full bg-white shadow transition-transform',
                sizeClasses.thumb,
                isSelected && sizeClasses.thumb.split(' ').find((c) => c.startsWith('selected:'))
              )}
              style={{
                transform: isSelected
                  ? `translateX(${size === 'sm' ? '16px' : '20px'})`
                  : 'translateX(0)',
              }}
            />
          </div>
          {(label || description || children) && (
            <div className="flex flex-col">
              {label && (
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {label}
                </span>
              )}
              {description && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {description}
                </span>
              )}
              {children}
            </div>
          )}
        </>
      )}
    </AriaSwitch>
  )
}

export default Switch
